import React, { useState, useEffect, useCallback } from 'react';
import { Booking, Lesson } from '@court-sync/shared';
import { lessonService } from '../../services/lesson';
import { bookingService } from '../../services/booking';
import { LessonPaymentForm } from '../LessonPaymentForm';
import { Modal } from '../ui/Modal';
import { Alert, AlertDescription } from '../ui/Alert';
import { useAuthStore } from '../../store/auth';
import toast from 'react-hot-toast';

export type BookLessonPaymentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Booking to pay for (pending payment). */
  bookingId: string | null;
  /** Called after a successful payment so the parent can refresh the list. */
  onPaymentComplete?: () => void;
};

export function BookLessonPaymentModal({
  isOpen,
  onClose,
  bookingId,
  onPaymentComplete,
}: BookLessonPaymentModalProps) {
  const { user } = useAuthStore();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setLesson(null);
    setBooking(null);
    setError(null);
    setLoading(false);
  }, []);

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const bookingRes = await bookingService.getBooking(bookingId);
      const b = bookingRes.data;
      if (b.paymentStatus !== 'pending') {
        setBooking(b);
        setLesson(null);
        setError(
          b.paymentStatus === 'paid'
            ? 'This booking is already paid.'
            : 'Online payment is not available for this booking.'
        );
        return;
      }
      const lessonRes = await lessonService.getLesson(b.lessonId);
      setLesson(lessonRes.data);
      setBooking(b);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setError(
          'This payment link is for a different account. Sign in with the guardian account that requested this booking.'
        );
        toast.error('You do not have access to this booking');
      } else {
        const msg = e instanceof Error ? e.message : 'Failed to load booking';
        setError(msg);
      }
      setLesson(null);
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (isOpen && bookingId) {
      void load();
    } else if (!isOpen) {
      reset();
    }
  }, [isOpen, bookingId, load, reset]);

  const handlePaymentSuccess = async () => {
    if (booking) {
      try {
        await bookingService.updateBooking(booking.id, {
          status: 'confirmed',
          paymentStatus: 'paid',
        });
      } catch (err) {
        console.error('Error updating booking after payment:', err);
      }
    }
    toast.success('Payment successful! Your lesson is confirmed.');
    onPaymentComplete?.();
    onClose();
  };

  const handlePaymentError = (msg: string) => {
    toast.error(msg);
  };

  const handlePaymentCancel = () => {
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Complete payment" maxWidth="3xl">
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : null}

      {!loading && error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {!loading && lesson && booking && booking.paymentStatus === 'pending' && user ? (
        <LessonPaymentForm
          variant="modal"
          booking={booking}
          lesson={lesson}
          customerEmail={user.email || ''}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onCancel={handlePaymentCancel}
        />
      ) : null}
    </Modal>
  );
}
